/* istanbul ignore file */
import React from 'react';
import _, { mapObject, constant, compose, debounce, partial } from 'underscore';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Glyphicon } from '@bingads-webui-react/primitive';
import onClickOutside from 'react-onclickoutside';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { withI18nConsumer } from '@bingads-webui-react/i18n-context';
import { withSettingsConsumer } from '@bingads-webui-react/settings-context';
import { withUserConsumer } from '@bingads-webui-react/user-context';
import { ClientCenterCustomerService } from '@bingads-webui/clientcenter-customer-service';
import { withScope, QueryParam } from '@bingads-webui-react/scope-service';
import { perfMarkerPropTypes } from '@bingads-webui/performance-marker';
import { FocusLock } from '@bingads-webui-react/focus-lock';
import { getAccountBreadcrumbStyles } from '../../styles';
import { OverflowBreadcrumb } from './overflow-breadcrumb';
import { AccountSelectorPopOver } from './account-selector-popover';

/* Contains everything from the original index.js file except that AccountSelector
*  comes from a prop now. The intent is to pass in a faster version of
*  AccountSelector without API calls from CampaignUI to save on page load time.
*  The AccountBreadcrumb and StyledAccountBreadcrumb components in index.js
*  use the old AccountSelector. There should be no change at all from their old behavior.
*/

// TODO: the original AccountBreadcrumb code is duplicated
// because onClickOutside doesn't work well with composition and state changes.
// Find out a way to share handleClickOutside between both versions to not repeat code

const DEFAULT_DEBOUNCE_TIMEOUT_IN_MS = 500;
let debounceHide = null;

export class AccountBreadcrumbBase extends React.PureComponent {
  constructor(props) {
    super(props);
    if (!props.parentState) {
      this.state = {
        showDropdownMenu: false,
        showAccountSelectorView: false,
      };
    }
  }

  getSelectedEntity = () => {
    const {
      scope, account, userContext: { CurrentCustomer: customer },
    } = this.props;

    return _.result(scope.values, 'aid') > 0 ? {
      id: account.Id,
      name: account.Name,
      number: account.AccountNumber,
      isCustomer: false,
    } : {
      id: customer.Id,
      name: customer.Name,
      number: customer.Number,
      isCustomer: true,
    };
  };

  tryGetShowDropdownMenu() {
    if (this.props.parentState) {
      return this.props.parentState.showDropdownMenu;
    }
    return this.state.showDropdownMenu;
  }

  tryGetShowAccountSelectorView() {
    if (this.props.parentState) {
      return this.props.parentState.showAccountSelectorView;
    }
    return this.state.showAccountSelectorView;
  }

  trySetState(state) {
    if (this.props.setParentState) {
      this.props.setParentState(state);
    } else {
      this.setState(state);
    }
  }

  hide() {
    this.trySetState({ showDropdownMenu: false });
  }

  handleMouseEnter = () => {
    if (this.tryGetShowDropdownMenu() && debounceHide) {
      debounceHide.cancel();
      debounceHide = null;
    } else if (!this.tryGetShowDropdownMenu()) {
      this.trySetState({ showAccountSelectorView: false, showDropdownMenu: true });
    }
  };

  handleDropdownMenuMouseEnter = () => {
    if (this.tryGetShowDropdownMenu() && debounceHide) {
      debounceHide.cancel();
      debounceHide = null;
    }
  };

  handleMouseLeave = () => {
    if (!this.tryGetShowAccountSelectorView() && this.tryGetShowDropdownMenu()) {
      debounceHide = debounce(() => this.hide(), DEFAULT_DEBOUNCE_TIMEOUT_IN_MS);
      debounceHide();
    }
  };

  handleHeaderClick = () => {
    // close the dropdown on header click if it is already open
    const currentViewState = this.tryGetShowAccountSelectorView() && this.tryGetShowDropdownMenu();
    this.trySetState({
      showAccountSelectorView: !currentViewState,
      showDropdownMenu: !currentViewState,
    });
    if (debounceHide) {
      debounceHide.cancel();
      debounceHide = null;
    }
  };

  handleClickOutside = () => {
    if (this.tryGetShowAccountSelectorView() || this.tryGetShowDropdownMenu()) {
      this.hide();
    }
  };

  renderXXLHeader = () => {
    const { classes, i18n, isReadOnly } = this.props;
    const selectedEntity = this.getSelectedEntity();

    return (
      <React.Fragment>
        <div className={classes.topLine} >
          <OverflowBreadcrumb
            text={selectedEntity.name}
            customClassName={`${classes.topLeftText} breadcrumb-name`}
          />
        </div>
        <div className={classes.bottomLine}>
          <div className={classes.bottomText}>
            <span className={`${classes.bottomLeftText} breadcrumb-id-title`}>
              {selectedEntity.isCustomer ? i18n.getString(_TL_('Manager account')) : i18n.getString(_TL_('Account'))}
            </span>
            <OverflowBreadcrumb
              text={selectedEntity.number}
              customClassName={`${classes.bottomRightText} breadcrumb-id`}
            />
          </div>
          {!isReadOnly && <Glyphicon glyph="ba-ChevronDown" className={classes.bottomChevronDown} />}
        </div>
      </React.Fragment>
    );
  };

  renderXLHeader = () => {
    const { classes, i18n } = this.props;

    return (
      <React.Fragment>
        <Glyphicon glyph="ba-BulletedTreeList" className={classNames(classes.bulletedTreeListXLarge, 'accounts-glyph')} />
        <span className={classNames(classes.headerTextXLarge, 'accounts-title')}>{i18n.getString(_TL_('Accounts'))}</span>
      </React.Fragment>
    );
  };

  renderLHeader = () => {
    const { classes } = this.props;

    return (
      <Glyphicon glyph="ba-BulletedTreeList" className={classNames(classes.bulletedTreeListLarge, 'accounts-glyph')} />
    );
  };

  renderHeader = () => {
    const {
      classes, responsiveSize, i18n, isReadOnly,
    } = this.props;

    if (isReadOnly) {
      if (responsiveSize === 'xxl') {
        return (
          <div className={classNames([classes.header, classes.headerReadOnly])}>
            {this.renderXXLHeader()}
          </div>);
      }

      return null;
    }

    const dropdownToggleClasses = [
      classes.dropdownToggle,
      classes.toggleable,
      'global-menu-breadcrumb-dropdown-toggle',
      `global-menu-breadcrumb-dropdown-toggle-${responsiveSize}`,
      'dropdown-toggle',
    ];

    if (responsiveSize === 'xxl') {
      dropdownToggleClasses.push(classes.dropdownToggleWidthXXLarge);
    } else if (responsiveSize === 'xl') {
      dropdownToggleClasses.push(classes.dropdownToggleWidthXLarge);
    } else {
      dropdownToggleClasses.push(classes.dropdownToggleWidthLarge);
    }

    return (
      <button
        className={classNames(dropdownToggleClasses)}
        tabIndex="0"
        aria-label={i18n.getString(_TL_('Select an account to view its details'))}
        onClick={this.handleHeaderClick}
        onMouseEnter={this.handleMouseEnter}
        aria-haspopup
        aria-expanded={this.tryGetShowDropdownMenu()}
      >
        <div className={classes.header}>
          {responsiveSize === 'xxl' && this.renderXXLHeader()}
          {responsiveSize === 'xl' && this.renderXLHeader()}
          {responsiveSize === 'l' && this.renderLHeader()}
        </div>
      </button>
    );
  };

  renderMenu = () => {
    const {
      ccCustomerService, AccountSelector, perfMarker,
    } = this.props;

    // TODO: xiaoyl Currently switch account with scope service will not do full refresh
    // And the permission context will be wrong when switch account
    // This is a work around to use link href for full refresh
    // Will switch this back to scope service once CCMT team support the update for permissions.
    /* onClick={() => onBreadcrumbClick({ cid, aid }, scope)}
    const onBreadcrumbClick = ({ cid, aid }, scope) => {
      if (aid === null) {
        return;
      }
      scope.set({
        cid,
        aid,
      });

      scope.save();
    }; */

    const switchAccountsLabelClicked = () => this.trySetState({ showAccountSelectorView: true });

    return (
      <div
        className="global-menu-breadcrumb-dropdown-menu dropdown-menu react-dropdown-menu"
        onMouseEnter={this.handleDropdownMenuMouseEnter}
      >
        <FocusLock
          isControlledFocusLock
          isFocusLock={this.tryGetShowDropdownMenu()}
          onExit={() => this.hide()}
          focusFirstItemOnMount
        >
          <AccountSelectorPopOver
            AccountSelector={AccountSelector}
            ccCustomerService={ccCustomerService}
            selectedEntity={this.getSelectedEntity()}
            showAccountSelectorView={this.tryGetShowAccountSelectorView()}
            switchAccountsLabelClicked={switchAccountsLabelClicked}
            perfMarker={perfMarker}
          />
        </FocusLock>
      </div>
    );
  };

  render() {
    const { classes } = this.props;

    return (
      <div
        id="header-breadcrumb"
        className={classes.container}
        onMouseLeave={this.handleMouseLeave}
      >
        <div className={`accounts-dropdown dropdown ${this.tryGetShowDropdownMenu() ? ' open' : ''}`}>
          {this.renderHeader()}
          {this.renderMenu()}
        </div>
      </div>
    );
  }
}

AccountBreadcrumbBase.propTypes = {
  classes: PropTypes.shape(mapObject(
    getAccountBreadcrumbStyles({}, {}),
    constant(PropTypes.string.isRequired)
  )).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  userContext: PropTypes.shape({
    CurrentUser: PropTypes.object.isRequired,
  }).isRequired,
  settingsContext: PropTypes.shape({
    Urls: PropTypes.object.isRequired,
  }).isRequired,
  ccCustomerService: PropTypes.func,
  account: PropTypes.shape({
    Id: PropTypes.number.isRequired,
    Name: PropTypes.string.isRequired,
    AccountNumber: PropTypes.string.isRequired,
  }),
  responsiveSize: PropTypes.oneOf([
    'xxl',
    'xl',
    'l',
  ]).isRequired,
  scope: PropTypes.instanceOf(QueryParam).isRequired,
  AccountSelector: PropTypes.elementType.isRequired,
  perfMarker: perfMarkerPropTypes,
  parentState: PropTypes.shape({
    showDropdownMenu: PropTypes.bool,
    showAccountSelectorView: PropTypes.bool,
  }),
  setParentState: PropTypes.func,
  isReadOnly: PropTypes.bool,
};

AccountBreadcrumbBase.defaultProps = {
  account: {},
  ccCustomerService: param => new ClientCenterCustomerService(param),
  perfMarker: null,
  parentState: null,
  setParentState: null,
  isReadOnly: false,
};

export const StyledAccountBreadcrumbBase = compose(
  withScope,
  partial(withDefaultStyles, _, getAccountBreadcrumbStyles),
  withUserConsumer,
  withSettingsConsumer,
  withI18nConsumer,
  onClickOutside
)(AccountBreadcrumbBase);
